import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCar } from '@fortawesome/free-solid-svg-icons'
// ── palette & tokens ──────────────────────────────────────────────────────────
const C = {
  bg: '#F7F7F5',
  surface: '#FFFFFF',
  border: '#E8E6E1',
  muted: '#9B9890',
  text: '#1A1917',
  accent: '#2563EB',
  accentLight: '#EEF3FD',
  green: '#16A34A',
  greenLight: '#F0FDF4',
  red: '#DC2626',
};

// ── tiny design-system components ────────────────────────────────────────────
function Badge({ children, color = C.muted }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 8px', borderRadius: 99,
      background: C.bg, border: `1px solid ${C.border}`,
      fontSize: 11, fontWeight: 600, color, letterSpacing: '0.02em',
      fontFamily: 'inherit',
    }}>
      {children}
    </span>
  );
}

function Card({ children, style = {} }) {
  return (
    <div style={{
      background: C.surface, border: `1px solid ${C.border}`,
      borderRadius: 10, overflow: 'hidden', ...style,
    }}>
      {children}
    </div>
  );
}

function CardHeader({ children, action }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '10px 14px', borderBottom: `1px solid ${C.border}`,
      background: C.bg,
    }}>
      <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: C.muted }}>
        {children}
      </span>
      {action}
    </div>
  );
}

function Btn({ children, variant = 'primary', size = 'sm', ...props }) {
  const base = {
    display: 'inline-flex', alignItems: 'center', gap: 5,
    border: 'none', borderRadius: 6, cursor: 'pointer',
    fontFamily: 'inherit', fontWeight: 600, transition: 'opacity .15s',
    padding: size === 'xs' ? '3px 8px' : '5px 12px',
    fontSize: size === 'xs' ? 11 : 12,
  };
  const styles = {
    primary: { background: C.accent, color: '#fff' },
    ghost:   { background: 'transparent', color: C.accent, border: `1px solid ${C.border}` },
    green:   { background: C.green, color: '#fff' },
    dark:    { background: C.text, color: '#fff' },
  };
  return <button style={{ ...base, ...styles[variant] }} {...props}>{children}</button>;
}

// ── lightbox ──────────────────────────────────────────────────────────────────
function Lightbox({ src, onClose }) {
  const [scale, setScale] = useState(0.75);
  const imgRef = useRef(null);

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') onClose();
      if (e.key === '+' || e.key === '=') setScale(s => Math.min(3, +(s + 0.15).toFixed(2)));
      if (e.key === '-') setScale(s => Math.max(0.25, +(s - 0.15).toFixed(2)));
      if (e.key === '0') setScale(0.75);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 100,
      background: 'rgba(15,14,12,0.85)', backdropFilter: 'blur(4px)',
    }}>
      <div style={{ position: 'absolute', inset: 0 }} onClick={onClose} />

      {/* controls top-right */}
      <div style={{
        position: 'fixed', top: 14, right: 18, zIndex: 110,
        display: 'flex', gap: 8,
      }}>
        <a href={src} target="_blank" rel="noreferrer" style={{
          padding: '5px 12px', background: C.text, color: '#fff',
          borderRadius: 6, fontSize: 12, fontWeight: 600, textDecoration: 'none', fontFamily: 'inherit',
        }}>Open ↗</a>
        <button onClick={onClose} style={{
          padding: '5px 12px', background: C.surface, border: `1px solid ${C.border}`,
          borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
        }}>✕ Close</button>
      </div>

      {/* zoom controls bottom-right */}
      <div style={{
        position: 'fixed', bottom: 16, right: 18, zIndex: 110,
        display: 'flex', alignItems: 'center', gap: 6,
        background: C.surface, border: `1px solid ${C.border}`,
        borderRadius: 8, padding: '4px 8px',
      }}>
        <button onClick={() => setScale(s => Math.max(0.25, +(s * 0.8).toFixed(2)))} style={{ ...zoomBtn }}>−</button>
        <span style={{ fontSize: 11, fontWeight: 700, color: C.muted, minWidth: 36, textAlign: 'center', fontFamily: 'inherit' }}>{Math.round(scale * 100)}%</span>
        <button onClick={() => setScale(s => Math.min(3, +(s * 1.2).toFixed(2)))} style={{ ...zoomBtn }}>+</button>
        <button onClick={() => setScale(0.75)} style={{ ...zoomBtn, color: C.accent }}>⟳</button>
      </div>

      {/* image scroll area */}
      <div style={{ position: 'relative', zIndex: 105, height: '100vh', overflow: 'auto', padding: 48 }}>
        <div style={{ display: 'flex', justifyContent: 'center', minHeight: '100%' }}>
          <img
            ref={imgRef}
            src={src}
            alt="Race info"
            style={{
              display: 'block', maxWidth: 'none',
              transform: `scale(${scale})`, transformOrigin: 'top center',
              transition: 'transform 100ms ease',
            }}
          />
        </div>
      </div>
    </div>
  );
}
const zoomBtn = {
  border: 'none', background: 'none', cursor: 'pointer',
  fontSize: 14, fontWeight: 700, color: C.text, padding: '0 4px', fontFamily: 'inherit',
};

// ── rental modal ──────────────────────────────────────────────────────────────
function RentalModal({ raceId, onSave, onClose, currentUser, navigate }) {
  const [carModel, setCarModel] = useState('');
  const [store, setStore] = useState('');
  const now = new Date().toISOString().slice(0, 16);
  const later = new Date(Date.now() + 2 * 86400000).toISOString().slice(0, 16);
  const [rentalDate, setRentalDate] = useState(now);
  const [returnDate, setReturnDate] = useState(later);
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!currentUser) { onClose(); navigate('/'); return; }
    if (!carModel || !store) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/car_rentals', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ race_id: Number(raceId), car_model: carModel, store, rental_date: rentalDate, return_date: returnDate }),
      });
      if (res.ok) {
        const data = await res.json();
        onSave(data.rental || data);
      }
    } finally { setSaving(false); }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 80, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(15,14,12,0.5)' }} onClick={onClose} />
      <div style={{ position: 'relative', zIndex: 85, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 24, width: '100%', maxWidth: 420, boxShadow: '0 20px 60px rgba(0,0,0,0.15)', fontFamily: 'inherit' }}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 16, color: C.text }}>Add car rental</div>
        <div style={{ display: 'grid', gap: 10 }}>
          {[
            ['Car model', carModel, setCarModel, 'text', true],
            ['Store / pickup location', store, setStore, 'text', false],
          ].map(([label, val, setter, type, auto]) => (
            <div key={label}>
              <label style={{ fontSize: 11, fontWeight: 600, color: C.muted, display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</label>
              <input
                autoFocus={auto}
                type={type}
                value={val}
                onChange={e => setter(e.target.value)}
                style={{ width: '100%', padding: '7px 10px', border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 13, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
          ))}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {[['Pickup', rentalDate, setRentalDate], ['Return', returnDate, setReturnDate]].map(([label, val, setter]) => (
              <div key={label}>
                <label style={{ fontSize: 11, fontWeight: 600, color: C.muted, display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</label>
                <input type="datetime-local" value={val} onChange={e => setter(e.target.value)}
                  style={{ width: '100%', padding: '7px 10px', border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }} />
              </div>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 18, justifyContent: 'flex-end' }}>
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn variant="primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save rental'}</Btn>
        </div>
      </div>
    </div>
  );
}

// ── note editor ───────────────────────────────────────────────────────────────
function NoteEditor({ user, raceId, raceNotes, setRaceNotes, currentUser }) {
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem('token');
  const isMe = currentUser && Number(user.id) === Number(currentUser.id);
  const existing = raceNotes.find(n => String(n.user_id) === String(user.id));
  useEffect(() => { setDraft(existing?.notes || ''); }, [user.id]);

  async function save() {
    if (!isMe) return;
    setSaving(true);
    try {
      const res = await fetch(`/api/races/${raceId}/notes/${user.id}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ notes: draft }),
      });
      const data = await res.json();
      setRaceNotes(prev => [...(prev || []).filter(p => String(p.user_id) !== String(user.id)), data]);
    } finally { setSaving(false); }
  }

  return (
    <div style={{ marginTop: 8 }}>
      <textarea
        value={draft}
        onChange={e => setDraft(e.target.value)}
        disabled={!isMe}
        rows={3}
        style={{ width: '100%', padding: '8px 10px', border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12, fontFamily: 'inherit', resize: 'vertical', color: C.text, background: isMe ? '#fff' : C.bg, boxSizing: 'border-box' }}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
        {isMe
          ? <Btn variant="primary" size="xs" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save note'}</Btn>
          : <span style={{ fontSize: 11, color: C.muted }}>Only editable by this user</span>}
      </div>
    </div>
  );
}

// ── helpers ───────────────────────────────────────────────────────────────────
function fmtDate(s, opts = { day: 'numeric', month: 'short', year: 'numeric' }) {
  return s ? new Date(s).toLocaleDateString('en-US', opts) : '—';
}
function fmtDateTime(s) {
  return s ? new Date(s).toLocaleString('en-US', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';
}

// ── main component ────────────────────────────────────────────────────────────
export default function Race() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [race, setRace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attendees, setAttendees] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [rentals, setRentals] = useState([]);
  const [flights, setFlights] = useState([]);
  const [raceNotes, setRaceNotes] = useState([]);
  const [airportHotelRows, setAirportHotelRows] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [showRentalModal, setShowRentalModal] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch(`/api/races/${id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => { setRace(d); setLoading(false); }).catch(() => setLoading(false));
    fetch('/api/profile', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => { if (d?.user) setCurrentUser(d.user); }).catch(() => {});
  }, [id]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const h = { Authorization: `Bearer ${token}` };
    const get = (url, setter) => fetch(url, { headers: h }).then(r => r.json()).then(setter).catch(() => setter([]));
    get(`/api/races/${id}/attendees`, setAttendees);
    get(`/api/races/${id}/hotels`, setHotels);
    get(`/api/races/${id}/notes`, setRaceNotes);
    get(`/api/races/${id}/airport_hotels`, d => setAirportHotelRows(d || []));
    get(`/api/flights?race_id=${id}`, d => setFlights(d || []));
    fetch('/api/car_rentals', { headers: h }).then(r => r.json())
      .then(d => setRentals((d || []).filter(x => String(x.race_id) === String(id)))).catch(() => setRentals([]));
  }, [id]);

  if (loading) return <div style={{ padding: 32, fontFamily: 'inherit', color: C.muted }}>Loading…</div>;
  if (!race || race.msg) return <div style={{ padding: 32, fontFamily: 'inherit', color: C.muted }}>Race not found</div>;

  const img = race.race_track_image ? `/assets/race-tracks/${race.race_track_image}` : null;
  const isAttendee = currentUser && (attendees.some(a => String(a.id) === String(currentUser.id)) || localStorage.getItem('isAdmin') === 'true');

  function openLightbox(name) {
    setLightboxSrc(`/assets/race-info/${race.id}/${name}.png`);
  }

  async function refreshAirportHotels() {
    const token = localStorage.getItem('token');
    fetch(`/api/races/${id}/airport_hotels`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => setAirportHotelRows(d || []));
  }

  return (
    <div style={{ minHeight: '100vh', background: C.bg, fontFamily: "'DM Sans', system-ui, sans-serif", color: C.text }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Mono:wght@400;500&display=swap');
        * { box-sizing: border-box; }
        a { color: ${C.accent}; text-decoration: none; }
        a:hover { text-decoration: underline; }
        details > summary { list-style: none; }
        details > summary::-webkit-details-marker { display: none; }
      `}</style>

      {/* ── Header ── */}
      <div style={{ background: C.surface, borderBottom: `1px solid ${C.border}` }}>
        <div style={{ maxWidth: 960, margin: '0 auto', padding: '14px 20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 16, alignItems: 'center' }}>

            {/* left: name + meta */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
                <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, lineHeight: 1.2 }}>{race.name}</h1>
                <Badge>{race.location}</Badge>
              </div>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12, color: C.muted }}>
                <span>📅 {fmtDate(race.start_date)} – {fmtDate(race.end_date)}</span>
              </div>

              {/* info buttons */}
              <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
                <Btn variant="primary" onClick={() => openLightbox('time-schedule')}>Time schedule</Btn>
                <Btn variant="ghost"   onClick={() => openLightbox('track-info')}>Track info</Btn>
                <Btn variant="ghost"   onClick={() => openLightbox('garage-info')}>Garage info</Btn>
                <Link to={`/races/${id}/attendees`} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 12px', background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12, fontWeight: 600, color: C.text }}>
                  All attendees ↗
                </Link>
                <Link to={`/races/${id}/travel-summary`} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 12px', background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12, fontWeight: 600, color: C.text }}>
                  ✈ Travel Summary
                </Link>
              </div>
            </div>
            {/* right: track image */}
            {img && (
              <div style={{ width: 140, height: 90, borderRadius: 8, overflow: 'hidden', border: `1px solid ${C.border}`, background: C.bg, flexShrink: 0 }}>
                <img src={img} alt={race.name} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Body ── */}
      <div style={{ maxWidth: 960, margin: '0 auto', padding: '16px 20px', display: 'grid', gap: 14 }}>

        {/* ── Attendees table ── */}
        <Card>
          <CardHeader>Attendees ({attendees.length})</CardHeader>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: C.bg }}>
                  {['Name', 'Flight(s)', 'Airport hotel', 'Notes'].map(h => (
                    <th key={h} style={{ padding: '7px 12px', textAlign: 'left', fontWeight: 700, fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase', color: C.muted, borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {attendees.map((u, i) => {
                  const userFlights = flights.filter(f => f.user_id === u.id).sort((a, b) => new Date(a.departure_time) - new Date(b.departure_time));
            
                  const urNote = raceNotes.find(n => String(n.user_id) === String(u.id));
                  const note = urNote?.notes || '';
                  const ah = airportHotelRows.find(a => String(a.user_id) === String(u.id));
                  const ahLegacy = raceNotes.find(n => String(n.user_id) === String(u.id));
                  const hasAH = Boolean(ah) || Boolean(ahLegacy?.airport_hotel);
                  const isMe = currentUser && Number(currentUser.id) === Number(u.id);
                  const token = localStorage.getItem('token');

                  return (
                    <tr
                      key={u.id}
                      onClick={() => setSelectedUser(prev => prev?.id === u.id ? null : u)}
                      style={{
                        borderBottom: `1px solid ${C.border}`,
                        background: selectedUser?.id === u.id ? C.accentLight : (i % 2 === 0 ? '#fff' : C.bg),
                        cursor: 'pointer', transition: 'background .12s',
                      }}
                    >
                      {/* name */}
                      <td style={{ padding: '8px 12px', fontWeight: 600 }}>
                        <Link to={`/users/${u.id}`} onClick={e => e.stopPropagation()} style={{ color: C.text }}>{u.name}</Link>
                      </td>

                      {/* flights */}
                      <td style={{ padding: '8px 12px', color: C.muted }}>
                        {userFlights.length === 0 ? '—' : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {userFlights.map((f, idx) => (
                              <div key={idx}>
                                <span style={{ fontWeight: 600, color: C.text, fontFamily: "'DM Mono', monospace", fontSize: 11 }}>{f.flight_number}</span>
                                <span style={{ color: C.muted, marginLeft: 4 }}>{f.departure?.split(' - ')[0]} → {f.arrival?.split(' - ')[0]}</span>
                                <div style={{ fontSize: 10, color: C.muted }}>{fmtDateTime(f.departure_time)} → {fmtDateTime(f.arrival_time)}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </td>


                      {/* airport hotel */}
                      <td style={{ padding: '8px 12px' }} onClick={e => e.stopPropagation()}>
                        {isMe ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            {['No', 'Yes'].map(label => {
                              const checked = label === 'Yes' ? hasAH : !hasAH;
                              return (
                                <label key={label} style={{ display: 'flex', alignItems: 'center', gap: 3, cursor: 'pointer', fontSize: 12 }}>
                                  <input type="radio" name={`ah_${u.id}`} checked={checked} onChange={async () => {
                                    if (label === 'No') {
                                      await fetch(`/api/races/${id}/airport_hotels/${u.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
                                    } else {
                                      await fetch(`/api/races/${id}/airport_hotels/${u.id}`, {
                                        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                                        body: JSON.stringify({ airport_hotel_id: null, notes: '' }),
                                      }).catch(() => {});
                                    }
                                    refreshAirportHotels();
                                  }} />
                                  {label}
                                </label>
                              );
                            })}
                            {hasAH && <Btn variant="primary" size="xs" onClick={() => navigate(`/airport-hotel/${id}/${u.id}`)}>Go →</Btn>}
                          </div>
                        ) : hasAH ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ color: C.green, fontWeight: 600, fontSize: 12 }}>Yes</span>
                            <Btn variant="primary" size="xs" onClick={e => { e.stopPropagation(); navigate(`/airport-hotel/${id}/${u.id}`); }}>Go →</Btn>
                          </div>
                        ) : <span style={{ color: C.muted }}>No</span>}
                      </td>

                      {/* notes */}
                      <td style={{ padding: '8px 12px', color: C.muted, maxWidth: 160 }}>
                        <span style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{note || '—'}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>

        {/* ── Rented Cars ── */}
        <Card>
          <CardHeader
            action={isAttendee && (
              <Btn variant="green" size="xs" onClick={() => setShowRentalModal(true)}>+ Add rental</Btn>
            )}
          >
            Rented Cars · {rentals.length}
          </CardHeader>
          {rentals.length === 0
            ? <div style={{ padding: '12px 14px', fontSize: 12, color: C.muted }}>No car rentals for this race.</div>
            : (
              <div style={{ display: 'grid', gap: 0 }}>
                {rentals.map((r, i) => {
                  const owner = attendees.find(a => a.id === r.user_id);
                  return (
                    <div key={r.id} style={{
                      display: 'grid', gridTemplateColumns: 'auto 1fr auto',
                      gap: 12, alignItems: 'center', padding: '9px 14px',
                      borderBottom: i < rentals.length - 1 ? `1px solid ${C.border}` : 'none',
                    }}>
                      <div style={{ width: 32, height: 32, borderRadius: '50%', background: C.accentLight, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>
                        <FontAwesomeIcon icon={faCar} />
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 12 }}>{owner?.name || `User ${r.user_id}`}</div>
                        <div style={{ fontSize: 11, color: C.muted }}>{r.car_model} · {r.store}</div>
                      </div>
                      <div style={{ fontSize: 11, color: C.muted, textAlign: 'right', fontFamily: "'DM Mono', monospace" }}>
                        {fmtDate(r.rental_date, { day: 'numeric', month: 'short' })} → {fmtDate(r.return_date, { day: 'numeric', month: 'short' })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
        </Card>

        {/* ── Selected user panel ── */}
        {selectedUser && (
          <Card style={{ borderLeft: `3px solid ${C.accent}` }}>
            <CardHeader action={<button onClick={() => setSelectedUser(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontSize: 16, lineHeight: 1 }}>✕</button>}>
              {selectedUser.name}
            </CardHeader>
            <div style={{ padding: '12px 14px', display: 'grid', gap: 14 }}>

              {/* profile */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <img src={selectedUser.profile_pic ? `/assets/profile-pic/${selectedUser.profile_pic}` : '/'} alt={selectedUser.name}
                  style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', border: `1px solid ${C.border}` }} />
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13 }}>{selectedUser.name}</div>
                  <div style={{ fontSize: 11, color: C.muted }}>{selectedUser.email}</div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                {/* hotels */}
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: C.muted, marginBottom: 6 }}>Hotels</div>
                  {hotels.filter(h => h.user_id === selectedUser.id).length === 0
                    ? <div style={{ fontSize: 12, color: C.muted }}>No hotels</div>
                    : hotels.filter(h => h.user_id === selectedUser.id).map(h => (
                      <div key={h.id} style={{ fontSize: 12, marginBottom: 4 }}>
                        <div style={{ fontWeight: 600 }}>{h.hotel_name}</div>
                        <div style={{ color: C.muted }}>{fmtDate(h.check_in)} – {fmtDate(h.check_out)}</div>
                        <div style={{ color: C.muted }}>{h.address}</div>
                      </div>
                    ))}
                </div>

                {/* car rentals */}
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: C.muted, marginBottom: 6 }}>Car rentals</div>
                  {rentals.filter(r => r.user_id === selectedUser.id).length === 0
                    ? <div style={{ fontSize: 12, color: C.muted }}>No rentals</div>
                    : rentals.filter(r => r.user_id === selectedUser.id).map(r => (
                      <div key={r.id} style={{ fontSize: 12, marginBottom: 4 }}>
                        <div style={{ fontWeight: 600 }}>{r.car_model}</div>
                        <div style={{ color: C.muted }}>{r.store}</div>
                        <div style={{ color: C.muted, fontFamily: "'DM Mono', monospace", fontSize: 11 }}>{fmtDateTime(r.rental_date)} → {fmtDateTime(r.return_date)}</div>
                      </div>
                    ))}
                </div>
              </div>

              {/* flights */}
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: C.muted, marginBottom: 6 }}>Flights</div>
                {flights.filter(f => f.user_id === selectedUser.id).length === 0
                  ? <div style={{ fontSize: 12, color: C.muted }}>No flights</div>
                  : flights.filter(f => f.user_id === selectedUser.id).sort((a, b) => new Date(a.departure_time) - new Date(b.departure_time)).map(f => (
                    <div key={f.id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 6, paddingLeft: 8, borderLeft: `2px solid ${C.accent}` }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 12, fontFamily: "'DM Mono', monospace" }}>{f.flight_number}</div>
                        <div style={{ fontSize: 11, color: C.muted }}>{f.departure} → {f.arrival}</div>
                        <div style={{ fontSize: 10, color: C.muted }}>{fmtDateTime(f.departure_time)} → {fmtDateTime(f.arrival_time)}</div>
                      </div>
                    </div>
                  ))}
              </div>

              {/* note editor */}
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: C.muted, marginBottom: 4 }}>Race note</div>
                <NoteEditor user={selectedUser} raceId={id} raceNotes={raceNotes} setRaceNotes={setRaceNotes} currentUser={currentUser} />
              </div>
            </div>
          </Card>
        )}
      </div>

      {/* ── Lightbox ── */}
      {lightboxSrc && <Lightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />}

      {/* ── Rental modal ── */}
      {showRentalModal && (
        <RentalModal
          raceId={id}
          currentUser={currentUser}
          navigate={navigate}
          onSave={rental => { setRentals(prev => [...prev, rental]); setShowRentalModal(false); }}
          onClose={() => setShowRentalModal(false)}
        />
      )}
    </div>
  );
}
